import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_QUICKBOOKS_PATH } from '../../constants.ts';
import { GET_QUICKBOOKS_INTEGRATIONS, UPDATE_QUICKBOOKS_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    apiKey: '',
    endUserId: '',
    orderChannelIds: [],
    templateId: '',
    orderTotalItemId: '',
    discountItemId: '',
    customerTotalDiscountCustomField: '',
};

const QuickBooksIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('quickbooks.title')}`;

    const { data, refetch } = useQuery(GET_QUICKBOOKS_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.quickbooksIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                endUserId: integration.endUserId,
                orderChannelIds: integration.orderChannels.map((c) => c.id),
                templateId: integration.templateId || '',
                orderTotalItemId: integration.orderTotalItemId || '',
                discountItemId: integration.discountItemId || '',
                customerTotalDiscountCustomField: integration.customerTotalDiscountCustomField || '',
            });
        }
    }, [integration]);

    let parsedExtraData;
    try {
        parsedExtraData = integration?.extraData ? JSON.parse(integration.extraData) : {};
    } catch (e) {
        parsedExtraData = {};
    }

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_QUICKBOOKS_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    apiKey: formData.apiKey,
                    endUserId: formData.endUserId,
                    orderChannels: formData.orderChannelIds,
                    templateId: formData.templateId || null,
                    orderTotalItemId: formData.orderTotalItemId || null,
                    discountItemId: formData.discountItemId || null,
                    customerTotalDiscountCustomField: formData.customerTotalDiscountCustomField || null,
                },
            });
            await refetch();
            publishToast({ description: t('quickbooks.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_QUICKBOOKS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('quickbooks.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('quickbooks.title'), to: url(CUSTOMER_INTEGRATION_QUICKBOOKS_PATH) },
                            { label: integration?.name || t('quickbooks.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleSubmit)}
                        title={t('quickbooks.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('quickbooks.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="apiKey"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('quickbooks.form.apiKey')}
                                    error={formErrors.apiKey}
                                    helpText={t('secretUpdateOnly')}
                                >
                                    <SecretField
                                        {...field}
                                        secretName="quickBooksApiKey"
                                        secretId={integration?.id}
                                        defaultIsVisible
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="endUserId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('quickbooks.form.endUserId')} error={formErrors.endUserId}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('quickbooks.form.orderChannelIds')}
                                    error={formErrors.orderChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="templateId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('quickbooks.form.templateId')} error={formErrors.templateId}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderTotalItemId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('quickbooks.form.orderTotalItemId')}
                                    error={formErrors.orderTotalItemId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="discountItemId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('quickbooks.form.discountItemId')}
                                    error={formErrors.discountItemId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="customerTotalDiscountCustomField"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('quickbooks.form.customerTotalDiscountCustomField')}
                                    error={formErrors.customerTotalDiscountCustomField}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <FormField label={t('quickbooks.form.extraData')}>
                            <pre className="inline-block text-sm rounded bg-surface-strong p-2.5 text-content-strong">
                                {JSON.stringify(parsedExtraData, null, 4)}
                            </pre>
                        </FormField>

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_QUICKBOOKS_PATH)}>
                                    {t('quickbooks.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default QuickBooksIntegrationEditPage;
